/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { ApiEndpoints } from 'services/endpoints';
import { apiService } from 'services/service';
import { useGetCountries } from './useGetLocations';
import toast from 'react-hot-toast';
import useGetUserDetails from './useGetUserDetail';

export const useCustomers = () => {
    const [customers, setCustomers] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const user = useGetUserDetails();

    const fetchCustomers = async (contact_name) => {
        const token = localStorage.getItem('token');
        const decodedToken = user?.flattenedUser?.attributes;
        console.log({ decodedToken });
        setIsLoading(true);
        try {
            const param = {
                contact_type: 'customer',
                cf_salesperson: decodedToken?.sales_person_id ?? decodedToken?.zoho_id,
            };
            if (contact_name) {
                param.contact_name = contact_name;
            }
            const queryParams = new URLSearchParams(param);
            const response = await apiService.get(`${ApiEndpoints.GET_CUSTOMERS}?${queryParams}`);

            setCustomers(response?.data?.contacts);
            return response?.data?.contacts;
        } catch (error) {
            console.log('Error from customers', error);
        } finally {
            setIsLoading(false);
        }
    };

    return { customers, fetchCustomers, isLoading };
};

export const useGetSalesPersons = () => {
    const [salespersons, setSalesPerson] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchSalesPerson = async () => {
        setIsLoading(true);
        try {
            const response = await apiService.get('/requests/crm/get-new-salesperson');

            setSalesPerson(response?.data?.data);
            return response?.data?.contacts;
        } catch (error) {
            console.log('Error from SalesPerson', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSalesPerson();
    }, []);

    return { salespersons, fetchSalesPerson, isLoading };
};

export const useVendors = () => {
    const [vendors, setVendors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const fetchVendors = async (contact_name) => {
        setIsLoading(true);
        try {
            const param = {
                contact_type: 'vendor',
                cf_user_type: 'B2B',
            };
            if (contact_name) {
                param.contact_name = contact_name;
            }
            const queryParams = new URLSearchParams(param);
            const response = await apiService.get(`${ApiEndpoints.GET_CUSTOMERS}?${queryParams}`);

            setVendors(
                response?.data?.contacts?.map((item) => ({ ...item, name: item.contact_name })),
            );
            return response?.data?.contacts;
        } catch (error) {
            console.log('Error from customers', error);
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        if (isLoading) return;
        if (!vendors) {
            fetchVendors();
        }
    }, []);

    return { vendors, fetchVendors, isLoading };
};

export function useVendorDetails(vendorId) {
    const [vendorData, setVendorData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!vendorId) return;
        let cancelled = false;

        const fetch = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const queryParams = new URLSearchParams({ contact_id: vendorId });
                const response = await apiService.get(
                    `${ApiEndpoints.GET_CUSTOMER_DETAILS}?${queryParams}`,
                );
                if (!cancelled) {
                    setVendorData(response?.data?.contact ?? response?.data ?? null);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to fetch vendor details:', err);
                    setError(err?.message || 'Failed to fetch vendor');
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        fetch();
        return () => {
            cancelled = true;
        };
    }, [vendorId]);

    const vendorMoq = useMemo(() => {
        if (!vendorData) return null;

        const hash = vendorData.custom_field_hash ?? vendorData;
        const fromHash =
            Number(hash?.cf_moq) ||
            Number(hash?.cf_vendor_moq) ||
            Number(hash?.cf_minimum_order) ||
            Number(hash?.cf_min_order_value) ||
            200;
        if (fromHash > 0) return fromHash;

        if (Array.isArray(vendorData.custom_fields)) {
            const moqField = vendorData.custom_fields.find((f) =>
                ['cf_moq', 'cf_vendor_moq', 'cf_minimum_order', 'cf_min_order_value'].includes(
                    f?.api_name,
                ),
            );
            const val = Number(moqField?.value);
            if (val > 0) return val;
        }

        return null;
    }, [vendorData]);

    return { vendorData, vendorMoq, isLoading, error };
}

export const useCustomersMutations = () => {
    const [isPending, setIsPending] = useState(false);

    const countries = useGetCountries();

    // Mutation to create a new customer
    const createCustomer = async (data) => {
        const country = countries?.find((country) => country.isoCode === data.country)?.name;
        const payload = {
            contact_name: `${data?.first_name} ${data?.last_name}`,
            customer_sub_type: 'individual',
            contact_type: 'customer',
            first_name: data?.first_name,
            last_name: data?.last_name,
            email: data?.email,
            phone: data?.phone,
            address: data?.line1,
            city: data?.city,
            state: data?.state,
            country,
        };
        setIsPending(true);
        try {
            const response = await apiService.post({ url: ApiEndpoints.CREATE_CUSTOMER, payload });

            return response?.data;
        } catch (error) {
            console.log('Error from creating customer', error);
            if (error.message.zoho_id) {
                toast.error('Customer already exists');
                return error.message;
            } else {
                toast.error(error?.message || 'Failed to create customer');
            }
        } finally {
            setIsPending(false);
        }
    };

    // Mutation to update an existing customer
    const updateCustomer = async (id, data) => {
        const country = countries?.find((country) => country.isoCode === data.country)?.name;
        const payload = {
            contact_name: `${data?.first_name} ${data?.last_name}`,
            contact_type: 'customer',
            first_name: data?.first_name,
            last_name: data?.last_name,
            billing_address: {
                address: data?.line1,
                city: data?.city,
                state: data?.state,
                country,
            },
            shipping_address: {
                address: data?.line1,
                city: data?.city,
                state: data?.state,
                country,
            },
            language_code: 'en',
        };
        setIsPending(true);
        try {
            const response = await apiService.put({
                url: `${ApiEndpoints.UPDATE_CUSTOMER}?contact_id=${id}`,
                payload,
            });

            return response?.data;
        } catch (error) {
            console.log('Error from updating customer', error);
            toast.error('Error updating customer');
        } finally {
            setIsPending(false);
        }
    };

    return { createCustomer, updateCustomer, isPending };
};
